import { ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { ZohoLinkedService, zohoAuthType } from 'app/model/linkedservice/zoho.linkedservice';
import { ZohoCopySource } from 'app/model/pipeline/copysource/zoho.copysource';
import { useEncryptedEndpointsRadioSwitch } from 'app/registries/linked-service/linked-service-shared-fields';
import { format } from 'app/shared/utils/string.util';
import { zohoReservedKeys } from 'app/shared/utils/connector-metadata-static-consts';
import { deprecatedConnectorDocLink } from 'app/shared/constants/links.const';

export const zohoMetadata: ILinkedServiceTypeMetadata = {
    type: Metamodel.modelFor(ZohoLinkedService).name,
    displayName: format(stringResources.ModelName_DisplayTextLegacyTemplate, stringResources.Zoho_DisplayText),
    linkedServiceKind: 'table',
    icon: {
        className: 'svg-i-100x100-zoho'
    },
    warningMessage: stringResources.Connector_Legacy_IsDeprecatedWarning,
    warningMessageHyperlink: deprecatedConnectorDocLink,
    warningTooltip: stringResources.Connector_Legacy_IsDeprecatedHint,
    notShowInTypeSelection: true,
    isFirstClass: true,
    supportDatasetV2: true,
    category: ['ServicesApps'],
    copySinkTypes: [],
    copySourceTypes: [Metamodel.modelFor(ZohoCopySource).name],
    controls: [
        {
            type: 'textbox',
            bind: 'endpoint',
            hide: '{ !isOldResource }',
            label: stringResources.ModelName_Endpoint,
            required: true,
            placeholder: stringResources.Zoho_Endpoint_Placeholder,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'accessToken',
            hide: '{ !isOldResource }',
            label: stringResources.ModelName_AccessToken,
            child: {
                type: 'password',
                label: stringResources.ModelName_AccessToken,
                isLabelVertical: true,
                required: true
            },
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Zoho_DisplayText),
                stringResources.ModelName_AccessToken)
        },
        {
            type: 'textbox',
            bind: 'endpoint',
            label: stringResources.ModelName_Endpoint,
            hide: '{ isOldResource }',
            required: true,
            placeholder: stringResources.Zoho_Endpoint_Placeholder,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true
        },
        {
            type: 'select-switch',
            bind: 'authType',
            label: stringResources.ModelName_AuthenticationType,
            hide: '{ isOldResource }',
            options: [
                {
                    label: stringResources.ModelName_AccessToken,
                    value: zohoAuthType.accessToken
                },
                {
                    label: 'OAuth 2.0',
                    value: zohoAuthType.OAuth_2_0
                }
            ],
            isLabelVertical: true,
            defaultValue: zohoAuthType.accessToken,
            cases: [
                {
                    caseValue: zohoAuthType.accessToken,
                    children: [
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'accessToken',
                            label: stringResources.ModelName_AccessToken,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_AccessToken,
                                isLabelVertical: true,
                                required: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Zoho_DisplayText),
                                stringResources.ModelName_AccessToken)
                        }
                    ]
                },
                {
                    caseValue: zohoAuthType.OAuth_2_0,
                    children: [
                        {
                            type: 'textbox',
                            bind: 'accountsUrl',
                            label: stringResources.ModelName_AccountsUrl,
                            placeholder: 'https://accounts.zoho.com',
                            isLabelVertical: true
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'accessToken',
                            label: stringResources.ModelName_AccessToken,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_AccessToken,
                                isLabelVertical: true,
                                required: false
                            },
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Zoho_DisplayText),
                                stringResources.ModelName_AccessToken)
                        },
                        {
                            type: 'textbox',
                            bind: 'clientId',
                            label: stringResources.ModelName_ClientId,
                            required: true,
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            isLabelVertical: true
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'clientSecret',
                            label: stringResources.ModelName_ClientSecret,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_ClientSecret,
                                required: true,
                                isLabelVertical: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'refreshToken',
                            label: stringResources.ModelName_RefreshToken,
                            child: {
                                type: 'password',
                                label: stringResources.ModelName_RefreshToken,
                                required: true,
                                isLabelVertical: true
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ]
                        }
                    ]
                }
            ]
        },
        useEncryptedEndpointsRadioSwitch,
        {
            type: 'key-val-list',
            bind: 'additionalFields',
            label: stringResources.AdditionalConnectionStringFields,
            hide: '{ isOldResource }',
            reservedKeys: zohoReservedKeys
        }
    ]
};
